import { useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import { useAppDispatch, useAppSelector } from "@app/hooks"
import {
  fetchConsultants,
  fetchRules,
  fetchSettings,
  selectRoutingSettings,
  updateSettings,
} from "@features/lead-routing/leadRouting.slice"
import { RulesPanel } from "./rulesPanel"
import { PreviewSection } from "./previewSection"
import { IncomingLeads } from "./incomingLeads"
import { ApiDialogButton } from "./apiDialog"
import { MyRoutingPanel } from "./myRoutingPanel"
import { Tab, Tabs } from "@mui/material"
import { selectLoggedInUser } from "@features/login/loginSelectors"
import { AsOf } from "./freshness"
import { SettingsDialog } from "./settingsDialog"

export function LeadRoutingPage() {
  const dispatch = useAppDispatch()
  const settingsState = useAppSelector(selectRoutingSettings)
  const user = useAppSelector(selectLoggedInUser)
  const roles: string[] = user?.roles ?? []
  // Only sales_admins manage routing (rules + engine mode). Everyone else who
  // can reach this tab — CCs, team leads — gets their read-only "My routing".
  const isAdmin = roles.includes("sales_admin")
  const [tab, setTab] = useState<"routing" | "mine">(
    isAdmin ? "routing" : "mine",
  )
  const [confirmMode, setConfirmMode] = useState<"rules" | "rotation" | null>(
    null,
  )
  const [settingsOpen, setSettingsOpen] = useState(false)
  // One global freshness signal: the top-bar refresh bumps reloadKey (which the
  // sections refetch on) and stamps asOf, replacing the old per-section clocks.
  const [reloadKey, setReloadKey] = useState(0)
  const [asOf, setAsOf] = useState<Date>(new Date())

  useEffect(() => {
    dispatch(fetchSettings())
    dispatch(fetchConsultants())
    dispatch(fetchRules())
  }, [dispatch])

  const settings =
    settingsState.status === "success" ? settingsState.data : undefined

  const reloadAll = () => {
    dispatch(fetchSettings())
    dispatch(fetchConsultants())
    dispatch(fetchRules())
    setReloadKey((key) => key + 1)
    setAsOf(new Date())
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        px: "24px",
        pb: "48px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1100,
          mx: "auto",
          pt: 3,
          minHeight: "72vh",
        }}
      >
        <Box>
          <Typography variant="h5">Lead Routing</Typography>
          <Typography variant="body2" color="text.secondary">
            Routes new admissions leads to Career Consultants. Every number here
            covers only leads routed by the API; owners set directly in HubSpot
            do not appear.
          </Typography>
        </Box>
        {/* Sticky toolbar: mode + freshness + settings stay in view while the
            long rules list scrolls underneath. */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mt={2}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            py: 1.5,
            bgcolor: "background.default",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ApiDialogButton />
            {/* Only admins can read or change the mode; a control a consultant
                cannot use must not render a state it cannot know. */}
            {isAdmin && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Assign by
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  disabled={!settings}
                  value={settings?.enabled ? "rules" : "rotation"}
                  onChange={(_, mode) => {
                    // Either direction changes how the next lead is assigned;
                    // both are confirmed deliberately.
                    if (mode) setConfirmMode(mode)
                  }}
                >
                  <ToggleButton value="rules">Rules</ToggleButton>
                  <ToggleButton value="rotation">Rotation</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}
          </Stack>
          {isAdmin && (
            <Stack direction="row" spacing={1} alignItems="center">
              <AsOf fetchedAt={asOf} onRefresh={reloadAll} />
              <Button
                size="small"
                startIcon={<SettingsOutlinedIcon fontSize="small" />}
                onClick={() => setSettingsOpen(true)}
                sx={{ textTransform: "none" }}
              >
                Settings
              </Button>
            </Stack>
          )}
        </Stack>

        {isAdmin && (
          <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ mt: 1 }}>
            <Tab label="Routing" value="routing" />
            <Tab label="My routing" value="mine" />
          </Tabs>
        )}
        <Box sx={{ mt: 3, pb: 6 }}>
          {isAdmin && tab === "routing" ? (
            <>
              {settings && !settings.enabled && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        dispatch(updateSettings({ enabled: true }))
                      }
                    >
                      Switch to Rules
                    </Button>
                  }
                >
                  Assignment runs by rotation right now. The rules below are
                  kept but match nothing until you switch back.
                </Alert>
              )}
              <Box
                sx={
                  settings && !settings.enabled
                    ? { opacity: 0.55, transition: "opacity .2s" }
                    : { transition: "opacity .2s" }
                }
              >
                <PreviewSection reloadKey={reloadKey} />
                <IncomingLeads reloadKey={reloadKey} />
                <RulesPanel reloadKey={reloadKey} />
              </Box>
            </>
          ) : (
            <MyRoutingPanel />
          )}
        </Box>

        <SettingsDialog
          open={settingsOpen}
          settings={settings}
          onClose={() => setSettingsOpen(false)}
        />

        <Dialog
          open={confirmMode !== null}
          onClose={() => setConfirmMode(null)}
          maxWidth="xs"
        >
          <DialogTitle>
            {confirmMode === "rules"
              ? "Assign by rules?"
              : "Assign by rotation?"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {confirmMode === "rules"
                ? "From the next lead on, the published rules decide who gets each lead, top to bottom. Leads no rule matches fall through to the Everyone else rotation. Switching back to Rotation stops rule matching instantly."
                : "Every rule stops matching immediately. From the next lead on, assignment goes to the least-recently-assigned consultant on shift, regardless of QTF, language or program. Your rules are kept and switching back to Rules re-activates them instantly."}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmMode(null)}>Cancel</Button>
            <Button
              variant="contained"
              color={confirmMode === "rules" ? "primary" : "warning"}
              onClick={() => {
                dispatch(updateSettings({ enabled: confirmMode === "rules" }))
                setConfirmMode(null)
              }}
            >
              {confirmMode === "rules"
                ? "Assign by rules"
                : "Assign by rotation"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  )
}
